import * as Icons from '@ant-design/icons';
import ConfirmationModal from 'components/modal/ConfirmationModal';
import dayjs, { Dayjs } from 'dayjs';
import {
  Alert,
  Button,
  DatePicker,
  Form,
  Input,
  Modal,
  Space,
  Table,
  Typography,
  message
} from 'antd';
import { ICourseAcademicCalendar } from 'interfaces/course_academic_calendar';
import { useEffect, useMemo, useRef, useState } from 'react';
import { HttpError, useCreate, useDelete, useList, useUpdate } from '@refinedev/core';
import { useParams } from 'react-router-dom';

interface TabAcademicCalendarProps {
  resourcePrefix: 'courses' | 'graduate/courses';
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canList: boolean;
}

interface FormValues {
  url: string;
  period: [Dayjs, Dayjs];
}

const DATE_FORMAT = 'YYYY-MM-DD';

const parseApiError = (error: unknown, fallback: string) => {
  const typedError = error as HttpError & {
    response?: { data?: { message?: string } };
  };

  if (typedError?.response?.data?.message) return typedError.response.data.message;
  if (typedError?.message) return typedError.message;

  return fallback;
};

const hasOverlap = (
  target: Pick<ICourseAcademicCalendar, 'starts_on' | 'ends_on'>,
  entries: ICourseAcademicCalendar[],
  excludeId?: string
) => {
  const targetStartsOn = dayjs(target.starts_on);
  const targetEndsOn = dayjs(target.ends_on);

  return entries.some(entry => {
    if (excludeId && entry.id === excludeId) {
      return false;
    }

    const entryStartsOn = dayjs(entry.starts_on);
    const entryEndsOn = dayjs(entry.ends_on);

    return (
      !targetEndsOn.isBefore(entryStartsOn, 'day') &&
      !targetStartsOn.isAfter(entryEndsOn, 'day')
    );
  });
};

export const TabAcademicCalendar = ({
  resourcePrefix,
  canCreate,
  canEdit,
  canDelete,
  canList
}: TabAcademicCalendarProps) => {
  const { id: courseId } = useParams();
  const [form] = Form.useForm<FormValues>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ICourseAcademicCalendar | null>(
    null
  );
  const [previewItem, setPreviewItem] = useState<ICourseAcademicCalendar | null>(
    null
  );
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [showPreviewFallback, setShowPreviewFallback] = useState(false);
  const previewTimeoutRef = useRef<number | null>(null);
  const hasPreviewLoadedRef = useRef(false);

  const clearPreviewTimeout = () => {
    if (previewTimeoutRef.current !== null) {
      window.clearTimeout(previewTimeoutRef.current);
      previewTimeoutRef.current = null;
    }
  };
  const [itemToDelete, setItemToDelete] =
    useState<ICourseAcademicCalendar | null>(null);
  const resource = `${resourcePrefix}/${courseId}/academic_calendars`;
  const isEditing = Boolean(editingItem);

  const { data, isLoading, refetch } = useList<ICourseAcademicCalendar>({
    resource,
    queryOptions: {
      enabled: Boolean(courseId && canList)
    },
    pagination: {
      mode: 'off'
    }
  });

  const { mutate: createCalendar, isLoading: isCreating } = useCreate();
  const { mutate: updateCalendar, isLoading: isUpdating } = useUpdate();
  const { mutate: deleteCalendar, isLoading: isDeleting } = useDelete();

  const calendars = useMemo(() => data?.data || [], [data?.data]);

  if (!courseId || !canList) {
    return null;
  }

  const openCreateModal = () => {
    setEditingItem(null);
    form.resetFields();
    setIsModalOpen(true);
  };

  const openEditModal = (item: ICourseAcademicCalendar) => {
    setEditingItem(item);
    form.setFieldsValue({
      url: item.url,
      period: [dayjs(item.starts_on), dayjs(item.ends_on)]
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingItem(null);
    form.resetFields();
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    const payload = {
      url: values.url,
      starts_on: values.period[0].format(DATE_FORMAT),
      ends_on: values.period[1].format(DATE_FORMAT)
    };

    if (hasOverlap(payload, calendars, editingItem?.id)) {
      message.error('Período de captação conflita com calendário existente');
      return;
    }

    const onSuccess = () => {
      message.success(
        isEditing
          ? 'Calendário acadêmico atualizado com sucesso'
          : 'Calendário acadêmico criado com sucesso'
      );
      closeModal();
      refetch();
    };

    const onError = (error: unknown) => {
      message.error(
        parseApiError(
          error,
          isEditing
            ? 'Não foi possível atualizar o calendário acadêmico'
            : 'Não foi possível criar o calendário acadêmico'
        )
      );
    };

    if (isEditing && editingItem) {
      updateCalendar(
        {
          resource,
          id: editingItem.id,
          values: payload,
          successNotification: false,
          errorNotification: false
        },
        { onSuccess, onError }
      );
      return;
    }

    createCalendar(
      {
        resource,
        values: payload,
        successNotification: false,
        errorNotification: false
      },
      { onSuccess, onError }
    );
  };

  const handleDelete = () => {
    if (!itemToDelete) {
      return;
    }

    deleteCalendar(
      {
        resource,
        id: itemToDelete.id,
        successNotification: false,
        errorNotification: false
      },
      {
        onSuccess: () => {
          message.success('Calendário acadêmico removido com sucesso');
          setItemToDelete(null);
          refetch();
        },
        onError: (error: unknown) => {
          message.error(
            parseApiError(error, 'Não foi possível remover o calendário acadêmico')
          );
        }
      }
    );
  };

  useEffect(() => {
    if (!previewItem?.url) {
      clearPreviewTimeout();
      hasPreviewLoadedRef.current = false;
      setIsPreviewLoading(false);
      setShowPreviewFallback(false);
      return;
    }

    hasPreviewLoadedRef.current = false;
    setIsPreviewLoading(true);
    setShowPreviewFallback(false);

    clearPreviewTimeout();
    previewTimeoutRef.current = window.setTimeout(() => {
      previewTimeoutRef.current = null;
      if (hasPreviewLoadedRef.current) {
        return;
      }
      setIsPreviewLoading(false);
      setShowPreviewFallback(true);
    }, 8000);

    return () => {
      clearPreviewTimeout();
    };
  }, [previewItem?.url]);

  return (
    <>
      <Space
        style={{
          width: '100%',
          justifyContent: 'space-between',
          marginBottom: 16
        }}
      >
        <Typography.Text type="secondary">
          Defina os períodos e links dos calendários acadêmicos do curso.
        </Typography.Text>
        <Button
          type="primary"
          icon={<Icons.PlusOutlined />}
          disabled={!canCreate}
          onClick={openCreateModal}
        >
          Novo calendário
        </Button>
      </Space>

      <Table<ICourseAcademicCalendar>
        rowKey="id"
        loading={isLoading}
        dataSource={calendars}
        pagination={false}
      >
        <Table.Column
          title="URL"
          dataIndex="url"
          key="url"
          render={value => (
            <a href={value} target="_blank" rel="noreferrer">
              {value}
            </a>
          )}
        />
        <Table.Column<ICourseAcademicCalendar>
          title="Período de captação"
          key="period"
          render={(_, record) =>
            `${dayjs(record.starts_on).format('DD/MM/YYYY')} - ${dayjs(
              record.ends_on
            ).format('DD/MM/YYYY')}`
          }
        />
        <Table.Column<ICourseAcademicCalendar>
          title="Ações"
          key="actions"
          align="right"
          render={(_, record) => (
            <Space>
              <Button
                icon={<Icons.EyeOutlined />}
                size="small"
                onClick={() => setPreviewItem(record)}
              />
              <Button
                icon={<Icons.EditOutlined />}
                size="small"
                disabled={!canEdit}
                onClick={() => openEditModal(record)}
              />
              <Button
                icon={<Icons.DeleteOutlined />}
                size="small"
                disabled={!canDelete}
                onClick={() => setItemToDelete(record)}
              />
            </Space>
          )}
        />
      </Table>

      <Modal
        title={
          isEditing ? 'Editar calendário acadêmico' : 'Novo calendário acadêmico'
        }
        open={isModalOpen}
        onOk={handleSubmit}
        onCancel={closeModal}
        confirmLoading={isCreating || isUpdating}
        okText={isEditing ? 'Salvar' : 'Criar'}
      >
        <Form<FormValues> form={form} layout="vertical">
          <Form.Item
            label="URL"
            name="url"
            rules={[
              { required: true, message: 'URL é obrigatória' },
              { type: 'url', message: 'Informe uma URL válida' }
            ]}
          >
            <Input type="url" placeholder="https://..." />
          </Form.Item>

          <Form.Item
            label="Período de captação"
            name="period"
            rules={[
              { required: true, message: 'Período de captação é obrigatório' }
            ]}
          >
            <DatePicker.RangePicker format="DD/MM/YYYY" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Visualizar calendário acadêmico"
        open={Boolean(previewItem)}
        onCancel={() => setPreviewItem(null)}
        footer={null}
        width={900}
      >
        {previewItem?.url && (
          <>
            <div
              style={{
                borderRadius: 12,
                overflow: 'hidden',
                border: '1px solid rgba(255, 255, 255, 0.12)',
                position: 'relative'
              }}
            >
              <iframe
                title="Calendário acadêmico"
                src={previewItem.url}
                onLoad={() => {
                  hasPreviewLoadedRef.current = true;
                  clearPreviewTimeout();
                  setIsPreviewLoading(false);
                  setShowPreviewFallback(false);
                }}
                style={{
                  width: '100%',
                  height: '70vh',
                  border: 0,
                  display: 'block',
                  borderRadius: 12
                }}
              />
              {isPreviewLoading && (
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(0, 0, 0, 0.35)'
                  }}
                >
                  <Typography.Text>Carregando pré-visualização...</Typography.Text>
                </div>
              )}
            </div>
            {showPreviewFallback && (
              <Alert
                style={{ marginTop: 12 }}
                type="warning"
                showIcon
                message="Não foi possível carregar o preview automaticamente."
                description={
                  <a href={previewItem.url} target="_blank" rel="noreferrer">
                    Clique aqui para abrir o calendário em uma nova aba.
                  </a>
                }
              />
            )}
            <Typography.Paragraph type="secondary" style={{ marginTop: 12 }}>
              Se o preview não carregar,{' '}
              <a href={previewItem.url} target="_blank" rel="noreferrer">
                abra o calendário em uma nova aba
              </a>
              .
            </Typography.Paragraph>
          </>
        )}
      </Modal>

      <ConfirmationModal
        open={Boolean(itemToDelete)}
        onCancel={() => setItemToDelete(null)}
        onOk={handleDelete}
        confirmLoading={isDeleting}
        okText="Excluir"
        cancelText="Cancelar"
        bodyTitle="Excluir calendário acadêmico"
        bodyText="Ao confirmar essa ação, ela não poderá ser desfeita. Deseja continuar?"
      />
    </>
  );
};
